import React, { useEffect, useState } from 'react';
import { Grid, Typography, styled, Box } from '@quillbot/design-system';
import { useDispatch, useSelector } from 'react-redux';
import { DEVICE_VIEWPORTS } from 'config/constants';
import { QuillDrawer, RootState } from '@Common/models/redux/store';
import QuillEditor, { QuillEditorIDS } from '@Common/handlers/quillEditor';
import DeleteInputText from '@Common/components/DeleteInputText';
import AddToChrome from '@Common/components/AddToChrome';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { useTranslate } from '@Common/hooks';
import GrammarCheckerDrawerControlBar from './GrammarCheckerDrawerControlBar';
import grammarbotTrack from '../handlers/track';
import { setGrammarCheckRunningAction } from '../actions/controlActions';
import { changeInputTextAction } from '../actions/userActions';

const StyledDesktopTitleBar = styled(Grid)`
  && {
    max-height: 63px;
    @media ${DEVICE_VIEWPORTS.xs} {
      display: none;
    }
  }
`;

const StyledGrammarTitle = styled(Grid)<{ quillDrawer: QuillDrawer | undefined }>`
  && {
    @media ${(props) => props.quillDrawer && '(max-width: 1550px)'}, (max-width: 1200px) {
      display: none;
    }
  }
`;

const StyledExtBtnWrapper = styled(Box)<{ sidebarPinned: boolean }>`
  && {
    opacity: ${(props) => (props.sidebarPinned ? 0 : 1)};
    transition: opacity 0.1s ease-in-out;
    @media (max-width: 1250px) {
      display: none;
    }
  }
`;

interface IPprops {
  showExtensionUpsellBanner?: boolean;
}

function TopControlBar({ showExtensionUpsellBanner }: IPprops) {
  const dispatch = useDispatch();
  const quillDrawer = useSelector((state: RootState) => state.common.quillDrawer);
  const isFeedbackDrawerOpen = quillDrawer === 'FEEDBACK';
  const isSettingDrawerOpen = ['GC_SETTINGS' || 'PPHR_SETTINGS'].includes(quillDrawer || '');
  const isStatsDrawerOpen = quillDrawer === 'GC_STATISTICS';
  const wordCount = useSelector((state: RootState) => state.grmr.input.wordCount);
  const independentTool = useSelector((state: RootState) => state.common.independentTool);
  const sidebarPinned = useSelector((state: RootState) => state.common.sidebarPinned);
  const { isDesktop, isIE, isExtension, isChromeExtInstalled } = useSelector(
    (state: RootState) => state.common.platform
  );
  const [showAddToChromeBtn, setShowAddToChromeBtn] = useState(false);

  const { translate } = useTranslate();

  useEffect(() => {
    if (typeof isChromeExtInstalled === 'boolean') {
      const addtoChromeBtn =
        QuillLocalStorage.getInstance().get('showExtensionUpsellBanner', 'true') === 'false' &&
        !isChromeExtInstalled &&
        !showExtensionUpsellBanner &&
        !independentTool;
      setShowAddToChromeBtn(addtoChromeBtn);
    }
  }, [isChromeExtInstalled, showExtensionUpsellBanner]);

  return (
    <>
      <StyledDesktopTitleBar
        container
        justifyContent="space-between"
        style={{ padding: `${wordCount > 0 ? '12px' : '13px'} 16px` }}
      >
        <Grid style={{ marginBottom: `${wordCount > 0 ? '0px' : '24px'}` }} />
        <StyledGrammarTitle
          quillDrawer={quillDrawer}
          item
          xs={2}
          sm
          style={{ alignSelf: 'center' }}
        >
          <Typography style={{ fontWeight: 'bold' }} component="h2">
            {translate('Grammar Checker')}
          </Typography>
        </StyledGrammarTitle>
        <Grid
          item
          xs
          // eslint-disable-next-line no-nested-ternary
          style={{ marginTop: showAddToChromeBtn ? '0' : wordCount > 0 ? '-4px' : '-5px' }}
        >
          <Grid container>
            <Grid item id="toolbar-location" />
          </Grid>
        </Grid>
        <Grid item xs>
          <Grid container justifyContent="flex-end" alignItems="center">
            {showAddToChromeBtn && (
              <StyledExtBtnWrapper sidebarPinned={sidebarPinned}>
                <AddToChrome
                  originComp="topControlBarGC"
                  onAddClick={() => setShowAddToChromeBtn(false)}
                />
              </StyledExtBtnWrapper>
            )}
            {wordCount > 0 && (
              <Grid item>
                <DeleteInputText
                  isQuilling={false}
                  style={{ padding: '0px', marginLeft: '8px' }}
                  clearInputText={() => {
                    QuillEditor.getInstance(QuillEditorIDS.GRAMMAR_EDITOR)?.setContent('');
                    dispatch(changeInputTextAction('', '', 0, 0));
                    dispatch(setGrammarCheckRunningAction(false));
                    grammarbotTrack.clickedDeleteButton();
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </StyledDesktopTitleBar>
      {isDesktop && !isExtension && (
        <div style={{ position: isIE ? 'absolute' : 'sticky', top: isIE ? '85px' : '0px' }}>
          <div
            style={{
              position: 'absolute',
              right: '-40px',
              top: '0px',
            }}
          >
            {!(
              isFeedbackDrawerOpen ||
              isSettingDrawerOpen ||
              isStatsDrawerOpen ||
              independentTool
            ) && <GrammarCheckerDrawerControlBar />}
          </div>
        </div>
      )}
    </>
  );
}

export default TopControlBar;
